import {FC, useEffect, useState} from 'react';
import {Grid, Container, Typography, Paper, Box} from '@mui/material';
import moment from 'moment';
import {
    PaymentMethod,
    PaypalStatuses,
    Plan,
    Subscription,
    SubscriptionStatuses,
    useCancelSubscriptionMutation,
    useCancelSubscriptionNowMutation,
    useCreateCustomSubscriptionMutation,
    usePlansQuery,
    User,
    useUpdateCustomSubscriptionMutation,
    useUserQuery,
} from '../../shared/types/generated';
import {useParams} from 'react-router-dom';
import {InputDefault} from '../../shared/components/inputs/InputDefault';
import {Loading} from '../../shared/components/Loading';
import {sanitizePlanFrequencies} from '../../shared/utils/utils';
import {TableColumnType} from '../../shared/components/table/table-types';
import {TableDefault} from '../../shared/components/table/TableDefault';
import {SubscriptionForTable} from '../subscription/subscription-types';
import {ButtonDefault} from '../../shared/components/ButtonDefault';
import {CreateCustomSubscriptionModal} from '../subscription/components/CreateCustomSubscriptionModal';
import {openSnackbarAction} from '../../shared/components/snackbar/snackbar-actions';
import {TypeMessage} from '../../shared/components/snackbar/snackbar-types';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {updateCustomSubscriptionSchema} from '../subscription/subscription-validator';
import {DEFAULT_TIMEZONE, PAYMENT_METHODS_CUSTOM_ALLOWED} from '../../shared/constants';
import {CustomTooltip} from '../../shared/components/tooltip/TooltipHelper';
import {SelectDefault} from '../../shared/components/inputs/SelectDefault';
import {DatePickerDefault} from '../../shared/components/inputs/DatePickerDefault';
import {handleFormErrors} from '../../shared/utils/errors';
import {getDateByTimezone} from "../../shared/utils";
import { UserSubscriberEventsComp } from './UserSubscriberEventsComp';

export const UserDetailsSubscription: FC = () => {
    const id = useParams<{ id: string }>().id;
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [plans, setPlans] = useState<Plan[]>([]);

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: {errors, isValid},
    } = useForm({resolver: yupResolver(updateCustomSubscriptionSchema)});

    const {data, loading} = useUserQuery({
        variables: {id: Number(id)},
        fetchPolicy: 'network-only',
        onCompleted(event) {
            const subs = event?.user?.subscriptions?.find(
                (sub) => sub?.status === 'ACTIVE',
            ) as Subscription | null;

            if (!subs) return;

            const plan = subs?.plan as Plan;

            const plansNoDuplicated = [...plans, plan].filter(
                (plan, index, self) => index === self.findIndex((t) => t.id === plan.id),
            );
            setPlans(plansNoDuplicated);
        },
        onError(error) {
            openSnackbarAction(
                'No se pudo obtener la información del usuario, ' + error.message,
                TypeMessage.ERROR,
            );
        },
    });

    usePlansQuery({
        variables: {
            where: {status: PaypalStatuses.Active},
        },
        onCompleted(event) {
            const plansNoDuplicated = [...plans, ...(event?.plans?.items as Plan[])].filter(
                (plan, index, self) => index === self.findIndex((t) => t.id === plan.id),
            );
            setPlans(plansNoDuplicated);
        },
        onError(error) {
            openSnackbarAction(
                'No se pudo obtener la información de los planes, ' + error.message,
                TypeMessage.ERROR,
            );
        },
    });

    const activeUserSubscription = data?.user?.subscriptions?.find(
        (sub) => sub?.status === 'ACTIVE',
    ) as Subscription;

    const isEditValid = PAYMENT_METHODS_CUSTOM_ALLOWED.includes(
        activeUserSubscription?.payment_method as PaymentMethod,
    );
    const planSelected = watch('plan');
    const planSelectedData = plans.find((plan) => plan?.id === parseInt(planSelected));
    const planOptions = plans.map((plan) => ({
        value: String(plan?.id),
        label: plan.title as string,
    }));

    useEffect(() => {
        if (!activeUserSubscription) return;

        setValue('plan', activeUserSubscription?.plan?.id?.toString() as string);
        setValue('status', activeUserSubscription?.status as string);
        setValue(
            'periodStartDate',
            moment(parseInt(activeUserSubscription?.period_start_date as string)).toISOString(),
        );
        setValue(
            'periodEndDate',
            moment(parseInt(activeUserSubscription?.period_end_date as string)).toISOString(),
        );
        setValue('transactionId', activeUserSubscription?.transaction_id ?? '');
        setValue('notes', activeUserSubscription?.notes);
        setValue('paymentMethod', activeUserSubscription?.payment_method as string);
        setValue('subscription_url',activeUserSubscription?.subscription_url as string)
    }, [activeUserSubscription, editMode, setValue]);

    const [callCreateSubscription, {loading: loadingCreate}] = useCreateCustomSubscriptionMutation({
        refetchQueries: ['User'],
        onCompleted: () => {
            setOpen(false);
            openSnackbarAction('Se ha creado la suscripción correctamente', TypeMessage.SUCCESS);
        },
        onError: (error) => {
            openSnackbarAction('No se pudo crear la suscripción, ' + error.message, TypeMessage.ERROR);
        },
    });

    const [callUpdateSubscription, {loading: loadingUpdate}] = useUpdateCustomSubscriptionMutation({
        refetchQueries: ['User'],
        onCompleted: () => {
            setEditMode(false);
            openSnackbarAction('Se ha actualizado la suscripción correctamente', TypeMessage.SUCCESS);
        },
        onError: (error) => {
            openSnackbarAction(
                'No se pudo actualizar la suscripción, ' + error.message,
                TypeMessage.ERROR,
            );
        },
    });

    const [callCancelSubscriptionNow, {loading: loadingCancelNow}] =
        useCancelSubscriptionNowMutation({
            refetchQueries: ['User'],
            onCompleted: () => {
                openSnackbarAction('La suscripción ha sido cancelada correctamente', TypeMessage.SUCCESS);
            },
            onError: () => {
                openSnackbarAction(
                    'Ha ocurrido un error al intentar cancelar la suscripción',
                    TypeMessage.ERROR,
                );
            },
        });

    const [callCancelRenewSubscription, {loading: loadingCancelRenew}] =
        useCancelSubscriptionMutation({
            refetchQueries: ['User'],
            onCompleted: () => {
                openSnackbarAction(
                    'Se ha cancelado la renovación de la suscripción correctamente',
                    TypeMessage.SUCCESS,
                );
            },
            onError: () => {
                openSnackbarAction(
                    'Ha ocurrido un error al intentar cancelar la renovación de la suscripción',
                    TypeMessage.ERROR,
                );
            },
        });

    const onFormSubmit = handleSubmit(
        async (dataForm) => {
            callUpdateSubscription({
                variables: {
                    data: {
                        subscription_id: activeUserSubscription?.id as number,
                        status: dataForm.status as SubscriptionStatuses,
                        period_start_date: moment(dataForm.periodStartDate).toISOString(),
                        period_end_date: moment(dataForm.periodEndDate).toISOString(),
                        plan_id: parseInt(dataForm.plan),
                        payment_method: dataForm.paymentMethod as PaymentMethod,
                        transaction_id: dataForm?.transactionId ?? '',
                        notes: dataForm?.notes ?? '',
                    },
                },
            });
        },
        (error) => {
            handleFormErrors(error);
        },
    );

    if (loading) {
        return (
            <Loading height="400px">
                <Typography>Cargando suscripciones...</Typography>
            </Loading>
        );
    }

    const COLUMNS: TableColumnType<SubscriptionForTable>[] = [
        {
            columnName: 'ID',
            columnValue: (item) => item?.id || '',
        },
        {
            columnName: 'PLAN',
            columnValue: (item) => item?.plan?.title || '',
        },
        {
            columnName: 'FECHA DE SUSCRIPCIÓN',
            columnValue: (item) =>getDateByTimezone(item.created_at as string, DEFAULT_TIMEZONE)
        },
        {
            columnName: 'N° DE TRANSACCIÓN',
            columnValue: (item) => item?.transaction_id || '',
        },
        {
            columnName: 'METODO DE PAGO',
            columnValue: (item) => item?.payment_method || '',
        },
        {
            columnName: 'ESTADO',
            columnValue: (item) => item?.status || '',
        },
        {
            columnName: 'FECHA DE INICIO',
            columnValue: (item) => getDateByTimezone(item.period_start_date as string, DEFAULT_TIMEZONE)
        },
        {
            columnName: 'FECHA DE FIN',
            columnValue: (item) => getDateByTimezone(item.period_end_date as string, DEFAULT_TIMEZONE)
        },
        {
            columnName: 'Notas',
            columnValue: (item) => item?.notes || '',
        },
    ];

    const subscriptions = (data?.user?.subscriptions || []) as Subscription[];

    const HeaderButtom = (): JSX.Element => {
        if (!activeUserSubscription) {
            return (
                <ButtonDefault variant="contained" color="primary" onClick={() => setOpen(true)}>
                    Crear suscripción
                </ButtonDefault>
            );
        }

        if (editMode) {
            return (
                <Box gap={1} display="flex">
                    <ButtonDefault variant="outlined" color="primary" onClick={() => setEditMode(false)}>
                        Cancelar
                    </ButtonDefault>
                    <ButtonDefault
                        variant="contained"
                        color="primary"
                        onClick={onFormSubmit}
                        isLoading={loadingUpdate}
                        disabled={!isValid}
                    >
                        Guardar cambios
                    </ButtonDefault>
                </Box>
            );
        }

        if (isEditValid) {
            return (
                <Box gap={1} display="flex">
                    <ButtonDefault
                        variant="outlined"
                        color="primary"
                        isLoading={loadingCancelNow}
                        onClick={() =>
                            callCancelSubscriptionNow({
                                variables: {
                                    data: {
                                        subscription_id: activeUserSubscription?.id as number,
                                    },
                                },
                            })
                        }
                    >
                        Cancelar suscripción
                    </ButtonDefault>
                    <ButtonDefault variant="contained" color="primary" onClick={() => setEditMode(true)}>
                        Editar suscripción
                    </ButtonDefault>
                </Box>
            );
        }

        if (activeUserSubscription?.is_cancel === true && activeUserSubscription.status === 'ACTIVE') {
            return (
                <ButtonDefault
                    variant="contained"
                    color="primary"
                    isLoading={loadingCancelNow}
                    onClick={() =>
                        callCancelSubscriptionNow({
                            variables: {
                                data: {subscription_id: activeUserSubscription?.id as number},
                            },
                        })
                    }
                >
                    Cancelar suscripción
                </ButtonDefault>
            );
        }

        return (
            <Box gap={1} display="flex">
                <ButtonDefault
                    variant="outlined"
                    color="primary"
                    isLoading={loadingCancelRenew}
                    onClick={() =>
                        callCancelRenewSubscription({
                            variables: {
                                data: {subscription_id: activeUserSubscription?.id as number},
                            },
                        })
                    }
                >
                    Cancelar renovación
                </ButtonDefault>
                <ButtonDefault
                    variant="contained"
                    color="primary"
                    isLoading={loadingCancelNow}
                    onClick={() =>
                        callCancelSubscriptionNow({
                            variables: {
                                data: {subscription_id: activeUserSubscription?.id as number},
                            },
                        })
                    }
                >
                    Cancelar suscripción
                </ButtonDefault>
            </Box>
        );
    };

    return (
        <Container maxWidth={'lg'}>
            <CreateCustomSubscriptionModal
                user={data?.user as User}
                open={open}
                loading={loadingCreate}
                handleClose={() => setOpen(false)}
                onSubmit={(event) => {
                    callCreateSubscription({
                        variables: {
                            data: {
                                period_start_date: moment(event.periodStartDate).toISOString(),
                                period_end_date: moment(event.periodEndDate).toISOString(),
                                transaction_id: event.transactionId ?? '',
                                plan_id: parseInt(event.plan),
                                user_id: data?.user?.id as number,
                                payment_method: event.paymentMethod as PaymentMethod,
                                notes: event.notes,
                            },
                        },
                    });
                }}
            />
            <Grid container spacing={2} component="form" noValidate autoComplete="off">
                <Grid item sx={{display: 'flex', justifyContent: 'space-between'}} xs={12}>
                    <Typography fontSize={{sx: '16px', md: '20px'}} fontWeight={600}>
                        Detalles de la suscripción
                        {activeUserSubscription?.is_cancel === true &&
                            activeUserSubscription.status === 'ACTIVE' && (
                                <CustomTooltip value={'Esta suscripción se cancelara en la proxima renovación'}/>
                            )}
                    </Typography>
                    <HeaderButtom/>
                </Grid>
                {!activeUserSubscription ? (
                    <Grid item xs={12}>
                        <Typography>No hay suscripción activa</Typography>
                    </Grid>
                ) : (
                    <>
                        <Grid item xs={12} md={6}>
                            {!editMode ? (
                                <InputDefault
                                    labelName="Plan"
                                    variant="outlined"
                                    value={activeUserSubscription?.plan?.title as string}
                                    inputProps={{readOnly: true}}
                                />
                            ) : (
                                <Controller
                                    name="plan"
                                    control={control}
                                    render={({field}) => (
                                        <SelectDefault
                                            {...field}
                                            fullWidth
                                            labelName="Plan"
                                            variant="outlined"
                                            type="number"
                                            placeholder="Selecciona un plan"
                                            options={planOptions}
                                            error={!!errors.plan}
                                            helperText={errors.plan?.message}
                                        />
                                    )}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {!editMode ? (
                                <InputDefault
                                    labelName="Status"
                                    value={activeUserSubscription?.status}
                                    variant="outlined"
                                    inputProps={{readOnly: true}}
                                />
                            ) : (
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({field}) => (
                                        <SelectDefault
                                            {...field}
                                            fullWidth
                                            labelName="Status"
                                            variant="outlined"
                                            options={Object.values(SubscriptionStatuses).map((status) => ({
                                                value: status,
                                                label: status,
                                            }))}
                                            error={!!errors.status}
                                            helperText={errors.status?.message}
                                        />
                                    )}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {!editMode ? (
                                <InputDefault
                                    labelName="Metodo de pago"
                                    value={activeUserSubscription?.payment_method}
                                    variant="outlined"
                                    inputProps={{readOnly: !editMode}}
                                />
                            ) : (
                                <Controller
                                    name="paymentMethod"
                                    control={control}
                                    render={({field}) => (
                                        <SelectDefault
                                            {...field}
                                            fullWidth
                                            labelName="Método de pago"
                                            variant="outlined"
                                            options={PAYMENT_METHODS_CUSTOM_ALLOWED.map((method) => ({
                                                value: method,
                                                label: method,
                                            }))}
                                            error={!!errors.paymentMethod}
                                            helperText={errors.paymentMethod?.message}
                                        />
                                    )}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {!editMode ? (
                                <InputDefault
                                    labelName="Frencuencia"
                                    value={
                                        activeUserSubscription?.plan?.frequency
                                            ? sanitizePlanFrequencies(activeUserSubscription?.plan?.frequency)
                                            : ''
                                    }
                                    variant="outlined"
                                    inputProps={{readOnly: true}}
                                />
                            ) : (
                                <InputDefault
                                    labelName="Frencuencia"
                                    value={
                                        planSelectedData?.frequency
                                            ? sanitizePlanFrequencies(planSelectedData?.frequency)
                                            : ''
                                    }
                                    variant="outlined"
                                    inputProps={{readOnly: true}}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {!editMode ? (
                                <InputDefault
                                    labelName="Fecha de inicio"
                                    value={moment(
                                        parseInt(activeUserSubscription?.period_start_date as string),
                                    ).format('DD/MM/YYYY')}
                                    variant="outlined"
                                    inputProps={{readOnly: !editMode}}
                                />
                            ) : (
                                <Controller
                                    name={'periodStartDate'}
                                    control={control}
                                    render={({field: {value, onChange}}) => (
                                        <DatePickerDefault
                                            labelName="Fecha de inicio"
                                            textFieldProps={{
                                                helperText: errors.periodStartDate?.message,
                                                error: !!errors.periodStartDate?.message,
                                                placeholder: 'DD/MM/AAAA',
                                            }}
                                            value={value && value !== '' ? moment(value as string) : null}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {!editMode ? (
                                <InputDefault
                                    labelName="Fecha de fin"
                                    value={moment(parseInt(activeUserSubscription?.period_end_date as string)).format(
                                        'DD/MM/YYYY',
                                    )}
                                    variant="outlined"
                                    inputProps={{readOnly: !editMode}}
                                />
                            ) : (
                                <Controller
                                    name={'periodEndDate'}
                                    control={control}
                                    render={({field: {value, onChange}}) => (
                                        <DatePickerDefault
                                            labelName="Fecha de fin"
                                            textFieldProps={{
                                                helperText: errors.periodEndDate?.message,
                                                error: !!errors.periodEndDate?.message,
                                                placeholder: 'DD/MM/AAAA',
                                            }}
                                            value={value && value !== '' ? moment(value as string) : null}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {!editMode ? (
                                <InputDefault
                                    labelName="Id de la transacción"
                                    value={activeUserSubscription?.transaction_id}
                                    variant="outlined"
                                    inputProps={{readOnly: !editMode}}
                                />
                            ) : (
                                <Controller
                                    name="transactionId"
                                    control={control}
                                    render={({field}) => (
                                        <InputDefault
                                            {...field}
                                            fullWidth
                                            labelName="Id de la transacción"
                                            variant="outlined"
                                            placeholder="Id de la transacción"
                                            error={!!errors.transactionId}
                                            helperText={errors.transactionId?.message}
                                        />
                                    )}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {!editMode ? (
                                <InputDefault
                                    labelName="Notas de la suscripción"
                                    value={activeUserSubscription?.notes}
                                    variant="outlined"
                                    inputProps={{readOnly: !editMode}}
                                />
                            ) : (
                                <Controller
                                    name="notes"
                                    control={control}
                                    render={({field}) => (
                                        <InputDefault
                                            {...field}
                                            fullWidth
                                            labelName="Notas de la suscripción"
                                            variant="outlined"
                                            placeholder="Notas de la suscripción"
                                            error={!!errors.notes}
                                            helperText={errors.notes?.message}
                                        />
                                    )}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {!editMode ? (
                                <InputDefault
                                    labelName="Url de la suscripción"
                                    value={activeUserSubscription?.subscription_url}
                                    variant="outlined"
                                    inputProps={{readOnly: !editMode}}
                                />
                            ) : (
                                <Controller
                                    name="subscription_url"
                                    control={control}
                                    render={({field}) => (
                                        <InputDefault
                                            {...field}
                                            fullWidth
                                            labelName="Url de la suscripción"
                                            variant="outlined"
                                            placeholder="Url de la suscripción"
                                            error={!!errors.subscription_url}
                                            helperText={errors.subscription_url?.message}
                                        />
                                    )}
                                />
                            )}
                        </Grid>
                    </>
                )}
            </Grid>
            <Grid container spacing={2} mt={2.5}>
                <Grid item xs={12}>
                    <Typography fontSize={{sx: '16px', md: '20px'}} fontWeight={600}>
                        Historial de suscripciones
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Paper variant="outlined">
                        <TableDefault
                            items={subscriptions}
                            count={data?.user?.subscriptions?.length || 0}
                            columns={COLUMNS}
                            loading={loading}
                            messageLoading={'Cargando historial de suscripciones...'}
                            messageEmpty="No hay historial de suscripciones"
                            page={0}
                            pageSize={data?.user?.subscriptions?.length || 0}
                        />
                    </Paper>
                </Grid>
            </Grid>
            <UserSubscriberEventsComp userId={parseInt(id as string)}/>
        </Container>
    );
};
